import { useEffect, useRef, useState } from 'react';

/**
 * Pass 17 — Spotify Now Playing overlay widget.
 *
 * Polls `/api/spotify/now-playing?token=…` every 5 seconds. When a
 * track is playing it slides in from the configured corner; when
 * playback stops or moves to a podcast/ad, it slides out and stays
 * hidden until the next track. Progress bar interpolates client-side
 * between polls so it ticks smoothly at 60fps without hammering the
 * Spotify API.
 *
 * Fields (all optional, defaults match the catalog entry):
 *   position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
 *   theme: 'dark' | 'light' | 'transparent'
 *   showProgress: boolean
 *   showAlbumArt: boolean
 */

type Position = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
type Theme = 'dark' | 'light' | 'transparent';

interface SpotifyFields {
  position?: Position;
  theme?: Theme;
  showProgress?: boolean;
  showAlbumArt?: boolean;
}

interface NowPlayingTrack {
  name: string;
  artist: string;
  album: string;
  albumArt: string | null;
  durationMs: number;
  progressMs: number;
  isPlaying: boolean;
  trackUrl: string | null;
}

interface NowPlayingResponse {
  playing: boolean;
  track?: NowPlayingTrack;
}

const POLL_INTERVAL_MS = 5_000;
const API_BASE = '';

export function SpotifyNowPlayingWidget({
  token,
  fields,
}: {
  token: string;
  fields: Record<string, unknown>;
}) {
  const position = (fields.position as Position) ?? 'bottom-left';
  const theme = (fields.theme as Theme) ?? 'dark';
  const showProgress = fields.showProgress !== false;
  const showAlbumArt = fields.showAlbumArt !== false;

  const [track, setTrack] = useState<NowPlayingTrack | null>(null);
  const [visible, setVisible] = useState(false);
  // Client-side interpolated progress (ms) so the bar moves between
  // polls without re-hitting the API. Snaps to the server value on
  // each successful poll.
  const [interpolatedProgress, setInterpolatedProgress] = useState(0);
  const lastSyncRef = useRef<{ atMs: number; progressMs: number; isPlaying: boolean }>({
    atMs: 0,
    progressMs: 0,
    isPlaying: false,
  });

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    async function poll() {
      try {
        const res = await fetch(
          `${API_BASE}/api/spotify/now-playing?token=${encodeURIComponent(token)}`,
        );
        if (!res.ok) {
          // 401 / 5xx — fall through to "nothing playing" view. The
          // dashboard will surface reauth state separately.
          if (!cancelled) {
            setVisible(false);
            setTrack(null);
          }
          return;
        }
        const json = (await res.json()) as NowPlayingResponse;
        if (cancelled) return;
        if (json.playing && json.track) {
          setTrack(json.track);
          setVisible(true);
          lastSyncRef.current = {
            atMs: Date.now(),
            progressMs: json.track.progressMs,
            isPlaying: json.track.isPlaying,
          };
          setInterpolatedProgress(json.track.progressMs);
        } else {
          setVisible(false);
        }
      } catch {
        if (!cancelled) setVisible(false);
      }
    }
    void poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token]);

  // Smooth-progress ticker: only runs while a track is visible &
  // playing; otherwise the bar holds at its last value.
  useEffect(() => {
    if (!visible || !track || !track.isPlaying || !showProgress) return;
    let raf = 0;
    function tick() {
      const { atMs, progressMs, isPlaying } = lastSyncRef.current;
      if (!isPlaying) return;
      const elapsed = Date.now() - atMs;
      setInterpolatedProgress(Math.min(progressMs + elapsed, track?.durationMs ?? 0));
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [visible, track, showProgress]);

  const palette = getThemePalette(theme);
  const posStyle = getPositionStyle(position, visible);

  if (!track) return null;

  const pct =
    track.durationMs > 0 ? Math.min(100, (interpolatedProgress / track.durationMs) * 100) : 0;

  return (
    <div
      style={{
        position: 'fixed',
        ...posStyle,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 12,
        minWidth: 280,
        maxWidth: 380,
        borderRadius: 14,
        background: palette.bg,
        color: palette.fg,
        boxShadow: palette.shadow,
        backdropFilter: palette.blur,
        WebkitBackdropFilter: palette.blur,
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        transition: 'transform 420ms cubic-bezier(0.22, 1, 0.36, 1), opacity 420ms ease',
        opacity: visible ? 1 : 0,
        pointerEvents: 'none',
      }}
    >
      {showAlbumArt && track.albumArt && (
        <img
          src={track.albumArt}
          alt=""
          width={56}
          height={56}
          style={{
            borderRadius: 8,
            flexShrink: 0,
            objectFit: 'cover',
            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
          }}
        />
      )}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <SpotifyMark color={palette.accent} />
          <span
            style={{ fontSize: 11, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            {track.isPlaying ? 'Now Playing' : 'Paused'}
          </span>
        </div>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {track.name}
        </div>
        <div
          style={{
            fontSize: 12,
            opacity: 0.75,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {track.artist}
        </div>
        {showProgress && (
          <div
            style={{
              marginTop: 4,
              height: 3,
              background: palette.barBg,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${pct}%`,
                height: '100%',
                background: palette.accent,
                transition: 'width 200ms linear',
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function SpotifyMark({ color }: { color: string }) {
  return (
    <svg width={13} height={13} viewBox="0 0 168 168" aria-hidden="true">
      <path
        fill={color}
        d="M83.996.277C37.747.277.253 37.77.253 84.019c0 46.251 37.494 83.741 83.743 83.741 46.254 0 83.744-37.49 83.744-83.741 0-46.246-37.49-83.738-83.745-83.738zm38.404 120.78a5.217 5.217 0 0 1-7.18 1.73c-19.662-12.01-44.414-14.73-73.564-8.07a5.222 5.222 0 0 1-6.249-3.93 5.213 5.213 0 0 1 3.926-6.25c31.9-7.291 59.263-4.15 81.337 9.34 2.46 1.51 3.24 4.72 1.73 7.18zm10.25-22.805c-1.89 3.075-5.91 4.045-8.98 2.155-22.51-13.839-56.823-17.846-83.448-9.764-3.453 1.043-7.1-.903-8.148-4.35-1.04-3.453.907-7.093 4.354-8.143C75.36 65.93 113.176 70.4 139.026 86.29c3.07 1.89 4.04 5.91 2.156 8.98zm.88-23.744c-26.99-16.031-71.52-17.505-97.289-9.684-4.138 1.255-8.514-1.081-9.768-5.219a7.835 7.835 0 0 1 5.221-9.771c29.581-8.98 78.756-7.245 109.83 11.202a7.823 7.823 0 0 1 2.74 10.733c-2.2 3.722-7.02 4.949-10.73 2.739z"
      />
    </svg>
  );
}

function getThemePalette(theme: Theme) {
  switch (theme) {
    case 'light':
      return {
        bg: 'rgba(255, 255, 255, 0.96)',
        fg: '#1a1a1a',
        accent: '#1DB954',
        shadow: '0 8px 24px rgba(0,0,0,0.18)',
        blur: 'blur(8px)',
        barBg: 'rgba(0,0,0,0.12)',
      };
    case 'transparent':
      return {
        bg: 'rgba(0, 0, 0, 0.35)',
        fg: '#ffffff',
        accent: '#1DB954',
        shadow: 'none',
        blur: 'blur(12px)',
        barBg: 'rgba(255,255,255,0.25)',
      };
    case 'dark':
    default:
      return {
        bg: 'rgba(18, 18, 18, 0.94)',
        fg: '#ffffff',
        accent: '#1DB954',
        shadow: '0 8px 24px rgba(0,0,0,0.45)',
        blur: 'blur(8px)',
        barBg: 'rgba(255,255,255,0.18)',
      };
  }
}

function getPositionStyle(position: Position, visible: boolean): Record<string, string | number> {
  const offset = 24;
  const hiddenOffset = 60;
  const slideX = visible ? 0 : -hiddenOffset;
  const slideXRight = visible ? 0 : hiddenOffset;
  switch (position) {
    case 'top-left':
      return { top: offset, left: offset, transform: `translateX(${slideX}px)` };
    case 'top-right':
      return { top: offset, right: offset, transform: `translateX(${slideXRight}px)` };
    case 'bottom-right':
      return { bottom: offset, right: offset, transform: `translateX(${slideXRight}px)` };
    case 'bottom-left':
    default:
      return { bottom: offset, left: offset, transform: `translateX(${slideX}px)` };
  }
}
